//* Libraries imports
import {
  XIcon,
  CheckCircleIcon,
  DownloadIcon,
  FileTextIcon,
} from "lucide-react";
import { toast } from "sonner";

//* Components imports
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Button,
  IconButton,
  Box,
} from "@mui/material";

//* Hooks Imports
import { useUpdateSale } from "@hooks/sales/mutations/useUpdateSale";

//* Types imports
import type { ISale } from "@/schemas";

type SalesReceiptModalProps = {
  open: boolean;
  onClose: () => void;
  onSuccess?: () => void;
  sale: ISale;
};

export function SalesReceiptModal({
  open,
  onClose,
  onSuccess,
  sale,
}: SalesReceiptModalProps) {
  const mutationUpdateSale = useUpdateSale();

  const receiptUrl = sale?.paymentDetails?.receiptUrl;

  if (!sale || !receiptUrl) return null;

  const isImage = /\.(jpg|jpeg|png|webp|gif)$/i.test(receiptUrl);

  const handleApprove = () => {
    mutationUpdateSale.mutate(
      {
        id: sale._id || sale.id!,
        data: {
          paymentStatus: "RECEIVED",
          saleDate: new Date().toISOString(),
        },
      },
      {
        onSuccess: () => {
          if (onSuccess) onSuccess();
          onClose();
        },
      },
    );
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: "12px",
          height: "85vh",
          display: "flex",
          flexDirection: "column",
        },
      }}
    >
      <div className="flex items-center justify-between p-4 border-b border-gray-100 bg-white">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-blue-50 rounded-lg text-blue-600">
            <FileTextIcon size={20} />
          </div>
          <div>
            <DialogTitle
              sx={{
                p: 0,
                fontWeight: "bold",
                color: "#0f172a",
                fontSize: "16px",
                lineHeight: "1.2",
              }}
            >
              Comprovante de Pagamento
            </DialogTitle>
            <span className="text-xs text-gray-500">
              Verifique o comprovante antes de aprovar
            </span>
          </div>
        </div>
        <IconButton
          onClick={onClose}
          size="small"
          className="text-gray-400 hover:text-gray-600"
        >
          <XIcon size={20} />
        </IconButton>
      </div>

      <DialogContent
        sx={{
          p: 0,
          flex: 1,
          bgcolor: "#F1F5F9",
          overflow: "hidden",
          position: "relative",
        }}
      >
        <Box
          sx={{
            width: "100%",
            height: "100%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            p: 2,
          }}
        >
          {isImage ? (
            <img
              src={receiptUrl}
              alt="Comprovante"
              className="max-w-full max-h-full object-contain rounded-lg shadow-sm border border-gray-200 bg-white"
            />
          ) : (
            <iframe
              src={receiptUrl}
              title="Comprovante"
              width="100%"
              height="100%"
              className="rounded-lg shadow-sm border border-gray-200 bg-white"
            />
          )}
        </Box>
      </DialogContent>

      <div className="p-4 border-t border-gray-100 bg-white flex justify-between items-center gap-3">
        <Button
          onClick={() => window.open(receiptUrl, "_blank")}
          variant="outlined"
          color="inherit"
          startIcon={<DownloadIcon size={18} />}
          sx={{
            textTransform: "none",
            borderRadius: "8px",
            borderColor: "#E2E8F0",
          }}
        >
          Baixar arquivo
        </Button>

        <div className="flex gap-3">
          <Button
            onClick={onClose}
            color="inherit"
            sx={{
              textTransform: "none",
              borderRadius: "8px",
              color: "#64748B",
            }}
          >
            Cancelar
          </Button>
          <Button
            onClick={handleApprove}
            variant="contained"
            color="success"
            disabled={mutationUpdateSale.isPending}
            startIcon={
              mutationUpdateSale.isPending ? null : (
                <CheckCircleIcon size={18} />
              )
            }
            sx={{
              textTransform: "none",
              borderRadius: "8px",
              fontWeight: 600,
              boxShadow: "none",
              "&:hover": { boxShadow: "none" },
            }}
          >
            {mutationUpdateSale.isPending
              ? "Aprovando..."
              : "Aprovar Pagamento"}
          </Button>
        </div>
      </div>
    </Dialog>
  );
}
